import React, { useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/layout/TopBar";
import Footer from "@/components/layout/Footer";
import {
  ArrowRight, Phone, Mail, Users, Anchor, Package, Zap, Star,
  Calendar, CheckCircle2, ChevronDown, Truck, Globe, Route, ShieldCheck,
} from "lucide-react";

const SERVICES = [
  {
    id: "standard-general",
    icon: Package,
    color: "#19e6c8",
    tag: "ROAD FREIGHT",
    title: "Standard / General",
    tagline: "Reliable local and regional freight at the best rates.",
    desc: "Ad-hoc - Local general freight deliveries at the best rates – pallets, crates, cases etc. Book at least a day ahead for business-hours delivery. Tail lift drop-off available.",
    features: [
      "Most affordable delivery option",
      "Local and regional coverage across Australia",
      "Minimum one business day advance booking",
      "Business hours delivery (Mon–Fri 7AM–5PM)",
      "Pallets, cartons, and freight of all sizes",
      "Saturday deliveries by appointment",
    ],
    bestFor: "Businesses with predictable freight needs that can be scheduled at least a day in advance.",
  },
  {
    id: "permanent-hire",
    icon: Users,
    color: "#8a5cff",
    tag: "MOST POPULAR",
    title: "Permanent Hire",
    tagline: "Your driver, your brand, your schedule.",
    desc: "A dedicated driver working exclusively for your business. No more worrying about fleet ownership, maintenance, insurance, or driver management. DRIVN handles it all while your driver operates as if they're part of your team. Clients are given a platform and interlinked driver app, where they can create, manage and allocate jobs to drivers, live track, chat, view/download real time PODs, consignment notes, record wait times etc., without having to manually create a driver run sheet. It works as if you have your own driver and Transport Management System, provided by Drivn Freight.",
    features: [
      "Dedicated driver assigned to your business",
      "Eliminates fleet ownership and maintenance costs",
      "Driver learns your routes, clients, and processes",
      "Flexible hours aligned to your business schedule",
      "Driver/ Equipment management and compliance handled by DRIVN",
      "Ideal for businesses with regular, ongoing freight needs",
    ],
    bestFor: "Businesses with consistent daily freight requirements looking to reduce operational overhead.",
  },
  {
    id: "lcl-airfreight",
    icon: Anchor,
    color: "#3b82f6",
    tag: "SEA & AIR",
    title: "LCL / Airfreight",
    tagline: "Port to door. Sea or sky.",
    desc: "Air and Sea freight transport specialists with vast industry experience handling time sensitive import pickups and export drop-offs, booking and managing timeslots.",
    features: [
      "Less-than-container load (LCL) sea freight",
      "Air freight pickups and deliveries",
      "Port pickups and drop-offs",
      "Real-time shipment milestones",
      "Experienced in customs requirements",
      "Trusted by importers and exporters alike",
    ],
    bestFor: "Businesses importing or exporting goods via sea or air who need reliable port and last-mile logistics.",
  },
  {
    id: "fcl",
    icon: Truck,
    color: "#6366f1",
    tag: "FULL CONTAINER",
    title: "FCL",
    tagline: "Full container load, handled end-to-end.",
    desc: "Wharf import and export for Full Container loads – Skel, Sideloader and Tilt-tray services handled efficiently with the help of our dedicated associate partners.",
    features: [
      "Full container load (FCL) coordination",
      "Port pickups and drop-offs",
      "Trusted relationships with shipping lines",
      "Secure handling of high-volume cargo",
      "Experienced in customs documentation",
      "End-to-end logistics management",
    ],
    bestFor: "Businesses with high-volume import or export requirements needing full container solutions.",
  },
  {
    id: "interstate-bdouble",
    icon: Route,
    color: "#f59e0b",
    tag: "NATIONWIDE",
    title: "Interstate B-double",
    tagline: "Long-haul, heavy payload. Done right.",
    desc: "Full b-double interstate loads handled with ease via our trusted and reliable affiliate partner company at affordable rates.",
    features: [
      "B-double and heavy vehicle freight",
      "Interstate delivery across all Australian states",
      "High payload capacity",
      "Experienced long-haul drivers",
      "Real-time tracking across the journey",
      "Transparent pricing with no hidden surcharges",
    ],
    bestFor: "Businesses needing to move large, heavy loads interstate efficiently and reliably.",
  },
  {
    id: "freight-forwarding",
    icon: Globe,
    color: "#22c55e",
    tag: "MULTI-MODAL",
    title: "Freight Forwarding Services",
    tagline: "Road, sea, and air — we coordinate it all.",
    desc: "Whether it's import–export, customs clearance or compliance – we make it all come together with our trusted partners.",
    features: [
      "Multi-modal freight coordination (road, sea, air)",
      "Customs clearance and documentation support",
      "Import and export freight management",
      "End-to-end supply chain visibility",
      "Experienced freight forwarding agents",
      "Tailored solutions for complex logistics needs",
    ],
    bestFor: "Businesses with international supply chains needing a single point of contact for all freight modes.",
  },
  {
    id: "same-day-express",
    icon: Zap,
    color: "#f97316",
    tag: "FAST",
    title: "Same Day Express",
    tagline: "Book today. Delivered today.",
    desc: "Urgency is our speciality. Book your delivery before midday and we'll ensure your freight is at its destination before close of business. Because we understand that in business, timing is everything.",
    features: [
      "Book same day, deliver same day",
      "Must be booked before midday for same-day guarantee",
      "Metro and surrounds coverage",
      "Real-time driver tracking",
      "Ideal for urgent business deliveries",
      "Confirmation within the hour of booking",
    ],
    bestFor: "Businesses facing unexpected delivery needs or time-sensitive freight that can't wait.",
  },
  {
    id: "vip",
    icon: Star,
    color: "#a855f7",
    tag: "3–4 HOURS",
    title: "VIP",
    tagline: "Time equals money. We know it.",
    desc: "Our fastest service tier. Confirmed bookings are collected and delivered within 3–4 hours. When you need something moved urgently and speed is non-negotiable, this is your solution.",
    features: [
      "Delivery within 3–4 hours of confirmed booking",
      "Highest priority assignment",
      "Available Mon–Fri during business hours",
      "Dedicated VIP driver dispatched immediately",
      "Live tracking from pickup to delivery",
      "Perfect for critical business freight",
    ],
    bestFor: "When freight is urgent, high-value, or time-critical and cannot wait for standard delivery windows.",
  },
  {
    id: "hourly-custom",
    icon: Calendar,
    color: "#ff6b6b",
    tag: "FLEXIBLE",
    title: "Hourly / Custom",
    tagline: "Your business, your rules.",
    desc: "Not every freight need fits a box. Our hourly hire, pallet rate, and customised trucking solutions are designed to flex around your unique requirements — whatever they look like.",
    features: [
      "Hourly driver hire for ongoing tasks",
      "Competitive pallet rates",
      "Customised trucking solutions",
      "Tailored routes and schedules",
      "Suitable for events, relocations, special projects",
      "Quote-based pricing for transparency",
    ],
    bestFor: "Businesses with unique, irregular, or complex freight requirements that don't fit standard tiers.",
  },
];

export default function Services() {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="min-h-screen relative overflow-x-hidden">

      {/* Background — site-wide dark satin backdrop (body bg); no video here. */}

      <div className="relative z-10">
        <TopBar />

        {/* ── HERO ──────────────────────────────────────────── */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-8 py-24 md:py-32">
          <div className="max-w-3xl fade-up">
            <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm">FREIGHT SOLUTIONS</p>
            <h1 className="brand-title text-4xl sm:text-5xl md:text-6xl mt-4 leading-tight">
              OUR SERVICES
            </h1>
            <p className="text-white/70 mt-6 text-xl leading-relaxed max-w-2xl">
              From affordable local delivery to urgent VIP express, interstate B-double, and dedicated permanent hire — DRIVN FREIGHT has a solution for every business need across Australia.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="tel:+61470344648" className="pill-btn flex items-center gap-2">
                <Phone size={16} /> Call to Book
              </a>
              <a
                href="mailto:hello@drivnfreight.com.au?subject=Free%20Quote%20Request"
                className="rounded-full border border-[#19e6c8]/40 hover:border-[#19e6c8] bg-[#19e6c8]/5 hover:bg-[#19e6c8]/10 px-6 py-3 font-display tracking-widest text-sm flex items-center gap-2 text-[#19e6c8] transition-all"
              >
                <Mail size={15} /> Email for free Quote
              </a>
              <Link to="/login" className="rounded-full border border-white/30 hover:border-white/55 px-6 py-3 font-display tracking-widest text-sm flex items-center gap-2 transition-all">
                CLIENT LOGIN <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

        {/* ── SERVICE CARDS ─────────────────────────────────── */}
        <section className="max-w-[1400px] mx-auto px-6 lg:px-8 pb-16">
          <div className="space-y-4">
            {SERVICES.map((svc) => {
              const Icon = svc.icon;
              const isOpen = expanded === svc.id;
              return (
                <div
                  key={svc.id}
                  className="rounded-2xl border border-white/10 hover:border-white/20 transition-all overflow-hidden backdrop-blur-md"
                  style={{ background: "rgba(255,255,255,0.025)" }}
                >
                  {/* Header row */}
                  <button
                    className="w-full text-left p-4 sm:p-7 flex items-center gap-3 sm:gap-5 group"
                    onClick={() => setExpanded(isOpen ? null : svc.id)}
                  >
                    <span
                      className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                      style={{ background: `${svc.color}18`, color: svc.color }}
                    >
                      <Icon size={24} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="brand-title text-xl group-hover:text-[#19e6c8] transition-colors">{svc.title}</h3>
                        <span
                          className="rounded-full px-3 py-1 text-xs font-display tracking-widest shrink-0"
                          style={{ background: `${svc.color}15`, color: svc.color, border: `1px solid ${svc.color}28` }}
                        >
                          {svc.tag}
                        </span>
                      </div>
                      <p className="text-white/55 text-base mt-1">{svc.tagline}</p>
                    </div>
                    <ChevronDown
                      size={20}
                      className="text-white/40 shrink-0 transition-transform duration-300"
                      style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0)" }}
                    />
                  </button>

                  {/* Expanded content */}
                  {isOpen && (
                    <div className="px-4 sm:px-7 pb-5 sm:pb-8 border-t border-white/10">
                      <div className="grid md:grid-cols-2 gap-8 pt-6">
                        <div>
                          <p className="text-white/70 text-xl leading-relaxed">{svc.desc}</p>
                          <div
                            className="mt-5 rounded-xl p-4 border border-white/10 backdrop-blur-md"
                            style={{ background: "rgba(255,255,255,0.02)" }}
                          >
                            <p className="text-white/40 text-xs font-display tracking-wider mb-2">BEST FOR</p>
                            <p className="text-white/70 text-xl leading-relaxed">{svc.bestFor}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-white/40 text-xs font-display tracking-wider mb-4">WHAT'S INCLUDED</p>
                          <ul className="space-y-3">
                            {svc.features.map((f, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: svc.color }} />
                                <span className="text-white/70 text-xl">{f}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* ── WHICH SERVICE? GUIDE ──────────────────────────── */}
        <section className="relative py-24">
          <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <p className="font-display tracking-[0.4em] text-[#19e6c8] text-sm">QUICK GUIDE</p>
              <h2 className="brand-title text-4xl md:text-5xl mt-3">Which Service Is Right for You?</h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
              {[
                { q: "I need a driver every day",              a: "Permanent Hire",             color: "#8a5cff", link: "#permanent-hire" },
                { q: "I need affordable local delivery",       a: "Standard / General",         color: "#19e6c8", link: "#standard-general" },
                { q: "I'm importing via sea or air",           a: "LCL / Airfreight",           color: "#3b82f6", link: "#lcl-airfreight" },
                { q: "I need a full container load",           a: "FCL",                        color: "#6366f1", link: "#fcl" },
                { q: "I need interstate heavy freight",        a: "Interstate B-double",        color: "#f59e0b", link: "#interstate-bdouble" },
                { q: "I need multi-modal forwarding",          a: "Freight Forwarding",         color: "#22c55e", link: "#freight-forwarding" },
                { q: "I need delivery today",                  a: "Same Day Express",           color: "#f97316", link: "#same-day-express" },
                { q: "I need it in under 4 hours",             a: "VIP",                        color: "#a855f7", link: "#vip" },
              ].map(({ q, a, color, link }) => (
                <a
                  key={q}
                  href={link}
                  className="rounded-2xl p-5 border border-white/10 hover:border-white/25 transition-all block backdrop-blur-md"
                  style={{ background: "rgba(255,255,255,0.025)" }}
                >
                  <p className="text-white/50 text-base mb-3 leading-relaxed">"{q}"</p>
                  <div className="flex items-center gap-2">
                    <ArrowRight size={18} className="shrink-0" style={{ color }} />
                    <p className="brand-title text-xl" style={{ color }}>{a}</p>
                  </div>
                </a>
              ))}
            </div>

            {/* CTA */}
            <div
              className="rounded-3xl p-10 md:p-14 border border-white/10 text-center"
              style={{ background: "linear-gradient(120deg, rgba(111,76,255,0.14), rgba(43,95,255,0.10))" }}
            >
              <Truck size={40} className="mx-auto mb-5 text-[#19e6c8]" />
              <h3 className="brand-title text-3xl md:text-4xl">Still Not Sure?</h3>
              <p className="text-white/65 mt-3 text-lg max-w-xl mx-auto">
                Call us directly and our team will recommend the right service for your business — no pressure, no jargon.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <a href="tel:+61470344648" className="pill-btn flex items-center gap-2 !text-base !px-8 !py-4">
                  <Phone size={18} /> +61 470 344 648
                </a>
                <a
                  href="mailto:hello@drivnfreight.com.au"
                  className="rounded-full border border-white/25 hover:border-[#19e6c8]/50 px-8 py-4 font-display tracking-widest text-sm transition-all flex items-center gap-2"
                >
                  <Mail size={18} /> hello@drivnfreight.com.au
                </a>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}
